import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { purchaseAPI, supplierAPI, inventoryAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const PurchaseForm = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();

  // Prevent scroll wheel from changing number input values
  const handleNumberInputWheel = (e) => {
    e.currentTarget.blur();
  };
  const [formData, setFormData] = useState({
    supplier_id: '',
    inventory_item_id: '',
    quantity: '',
    unit_price: '',
    purchase_date: new Date().toISOString().split('T')[0],
    status: 'Pending',
    notes: '',
  });
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSuppliers();
    loadInventoryItems();
    if (id) loadPurchase();
  }, [id]);

  const loadSuppliers = async () => {
    try {
      const response = await supplierAPI.getAll();
      setSuppliers(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading suppliers');
    }
  };

  const loadInventoryItems = async () => {
    try {
      const response = await inventoryAPI.getAll();
      setInventoryItems(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading inventory items');
    }
  };

  const getItemName = (item) => {
    return i18n.language === 'ar' && item.item_name_ar ? item.item_name_ar : item.item_name;
  };

  const loadPurchase = async () => {
    try {
      const response = await purchaseAPI.getOne(id);
      const data = response.data.data || response.data;
      setFormData({
        supplier_id: data.supplier_id || '',
        inventory_item_id: data.inventory_item_id || '',
        quantity: data.quantity || '',
        unit_price: data.unit_price || '',
        purchase_date: data.purchase_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        status: data.status || 'Pending',
        notes: data.notes || '',
      });
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      ...formData,
      total_cost: formData.quantity * formData.unit_price,
    };

    try {
      if (id) {
        await purchaseAPI.update(id, submitData);
      } else {
        await purchaseAPI.create(submitData);
      }
      navigate('/purchases');
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const totalCost = (formData.quantity * formData.unit_price).toFixed(2);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{id ? t('purchases.editPurchase') : t('purchases.addPurchase')}</h2>
        <p className="text-gray-600 mt-1">{t('purchases.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.supplier')} *</label>
            <select
              value={formData.supplier_id}
              onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('purchases.supplier')}</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.item')} *</label>
            <select
              value={formData.inventory_item_id}
              onChange={(e) => setFormData({ ...formData, inventory_item_id: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('purchases.item')}</option>
              {inventoryItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {getItemName(item)} ({item.item_code})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.quantity')} *</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                onWheel={handleNumberInputWheel}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.unitPrice')} *</label>
              <input
                type="number"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                onWheel={handleNumberInputWheel}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">{t('purchases.totalPrice')}: <span className="font-bold text-blue-600 text-lg">{t('common.currency')} {totalCost}</span></p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.purchaseDate')} *</label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('purchases.status')} *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Pending">{t('purchases.statuses.Pending')}</option>
                <option value="Ordered">{t('purchases.statuses.Ordered')}</option>
                <option value="Received">{t('purchases.statuses.Received')}</option>
                <option value="Cancelled">{t('purchases.statuses.Cancelled')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('common.notes')}
            />
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.loading') : id ? t('purchases.editPurchase') : t('purchases.addPurchase')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/purchases')}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseForm;
